import { useState, useEffect } from 'react'
import api from '../../services/api'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts'

const COLORS = ['#25D366', '#128C7E', '#ef4444', '#f59e0b', '#3b82f6']

function StatCard({ icon, label, value, sub, color = 'var(--primary)' }) {
  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: '18px 20px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value ?? '—'}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  )
}

export default function MetricasBot() {
  const [datos, setDatos] = useState(null)
  const [preguntas, setPreguntas] = useState([])
  const [rango, setRango] = useState(7)
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    setCargando(true)
    Promise.all([
      api.get('/metricas/bot', { params: { dias: rango } }),
      api.get('/metricas/bot/preguntas-sin-respuesta', { params: { dias: rango } }),
    ]).then(([d, p]) => {
      setDatos(d.data)
      setPreguntas(p.data)
    }).catch(() => {}).finally(() => setCargando(false))
  }, [rango])

  if (cargando) return <div className="loading-fullscreen"><div className="spinner" /></div>

  const d = datos || {}
  const tasaResolucion = d.totalChats > 0
    ? Math.round((d.resueltosBot / d.totalChats) * 100)
    : 0
  const tasaEscalados = d.totalChats > 0
    ? Math.round((d.escaladosHumano / d.totalChats) * 100)
    : 0

  const pieData = [
    { name: 'Resueltos por bot', value: d.resueltosBot || 0 },
    { name: 'Escalados a humano', value: d.escaladosHumano || 0 },
    { name: 'En curso', value: (d.totalChats || 0) - (d.resueltosBot || 0) - (d.escaladosHumano || 0) },
  ].filter(x => x.value > 0)

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      {/* Encabezado */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>🤖 Métricas del Bot</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>Rendimiento de la IA, resolución sin intervención humana y preguntas sin respuesta</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setRango(d)}
              style={{ padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                background: rango === d ? 'var(--primary)' : 'var(--bg)', color: rango === d ? 'white' : 'var(--text)' }}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPIs principales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard icon="✅" label="Tasa de resolución bot" value={`${tasaResolucion}%`}
          sub={`${d.resueltosBot || 0} de ${d.totalChats || 0} chats`} color="#16a34a" />
        <StatCard icon="👤" label="Escalados a humano" value={`${tasaEscalados}%`}
          sub={`${d.escaladosHumano || 0} conversaciones`} color="#d97706" />
        <StatCard icon="⚡" label="Tiempo resp. bot" value={d.tiempoRespuestaBot ? `${d.tiempoRespuestaBot}s` : '—'}
          sub="Promedio de primera respuesta" color="var(--primary)" />
        <StatCard icon="🧑‍💼" label="Tiempo resp. agente" value={d.tiempoRespuestaAgente ? `${d.tiempoRespuestaAgente}s` : '—'}
          sub="Tras tomar el chat" color="#6366f1" />
        <StatCard icon="❓" label="Sin respuesta" value={preguntas.length}
          sub="Preguntas que el bot no supo resolver" color="#ef4444" />
        <StatCard icon="💬" label="Mensajes totales bot" value={d.mensajesBot || 0}
          sub={`en los últimos ${rango} días`} />
      </div>

      <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Gráfica de chats por día */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, marginBottom: 16 }}>📊 Chats por día — bot vs humano</div>
          {d.porDia?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.porDia}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="bot" name="Bot" fill="#25D366" radius={[4,4,0,0]} />
                <Bar dataKey="humano" name="Humano" fill="#128C7E" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Sin datos para este período
            </div>
          )}
        </div>

        {/* Pie chart */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, marginBottom: 16 }}>🥧 Distribución de resolución</div>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                    dataKey="value" paddingAngle={3}>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} chats`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 8 }}>
                {pieData.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[i] }} />
                    {item.name}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Sin datos
            </div>
          )}
        </div>
      </div>

      {/* Tiempos de respuesta */}
      {d.tiemposPorDia?.length > 0 && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 20, border: '1px solid var(--border)', marginBottom: 24 }}>
          <div style={{ fontWeight: 700, marginBottom: 16 }}>⏱ Tiempo de respuesta promedio (segundos)</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={d.tiemposPorDia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [`${v}s`]} />
              <Legend />
              <Line type="monotone" dataKey="bot" name="Bot" stroke="#25D366" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="agente" name="Agente" stroke="#6366f1" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Preguntas sin respuesta */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700 }}>❓ Preguntas que el bot no supo responder</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Úsalas para mejorar tu Base de Conocimiento</div>
          </div>
          {preguntas.length > 0 && (
            <a href="/sesiones" style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
              + Agregar al KB →
            </a>
          )}
        </div>

        {preguntas.length === 0 ? (
          <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
            El bot pudo responder todas las preguntas en este período
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {preguntas.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', background: '#f9731622', borderRadius: 10, border: '1px solid #f9731644' }}>
                <div style={{ fontSize: 18, flexShrink: 0 }}>❓</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{p.pregunta}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {p.veces} {p.veces === 1 ? 'vez' : 'veces'} · último: {new Date(p.ultimaVez).toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                  </div>
                </div>
                <a href="/sesiones"
                  style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
                  + KB
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
